import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";

import type { CurrencyOptionItem } from "@/api/admin/types";
import {
  CustomLabel,
  FieldErrorMessage,
  LabelWithOptional,
} from "@/components/atoms";
import { DatePickerControl } from "@/components/molecules";
import { CurrencySymbol } from "@/components/molecules/CurrencySymbol";
import { FormControl, FormField, FormItem } from "@/components/ui/form";
import { cn } from "@/lib/utils";

import { FormattedInputField } from "./FormattingInput";
import {
  calculateCashToCreditConversionRate,
  type FundingCreditTermsFormType,
} from "./types";

type FundingCreditFieldsProps = {
  currency?: CurrencyOptionItem | null;
};

const amountLabel = (title: string, currency?: CurrencyOptionItem | null) => (
  <span className="flex items-center">
    {title} (
    <CurrencySymbol
      code={currency?.code}
      symbol={currency?.symbol}
      classes="h-3"
    />
    )
  </span>
);

const parseWholeNumber = (value: string) => {
  const cleaned = value.replace(/\D/g, "");

  if (cleaned === "") {
    return undefined;
  }

  const num = parseInt(cleaned, 10);
  return isNaN(num) ? undefined : num;
};

const parseDecimal = (value: string) => {
  if (value === "") {
    return undefined;
  }

  const num = Number(value);
  return isNaN(num) ? undefined : num;
};

const FundingCreditFields = ({ currency }: FundingCreditFieldsProps) => {
  const form = useFormContext<FundingCreditTermsFormType>();
  const { control, watch, getValues, setValue } = form;
  const lastAutoRate = useRef<number | undefined>(undefined);

  const cashGranted = watch("cashGranted");
  const foodCredit = watch("requestedAmount");

  useEffect(() => {
    const auto = calculateCashToCreditConversionRate(foodCredit, cashGranted);
    const current = getValues("cashToCreditConversionRate");
    const shouldUpdate =
      current === undefined ||
      (lastAutoRate.current !== undefined &&
        typeof current === "number" &&
        Math.abs(current - lastAutoRate.current) < 1e-9);

    lastAutoRate.current = auto;

    if (shouldUpdate) {
      setValue("cashToCreditConversionRate", auto, {
        shouldValidate: true,
        shouldDirty: false,
      });
    }
  }, [cashGranted, foodCredit, getValues, setValue]);

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 items-start">
        <FormField
          control={control}
          name="cashGranted"
          render={({ field }) => (
            <FormItem className={cn("relative h-fit w-full gap-1 mb-5.5")}>
              <CustomLabel
                htmlFor="cashGranted"
                label={amountLabel("Cash granted", currency)}
              />
              <FormControl>
                <FormattedInputField
                  nameField="cashGranted"
                  field={field}
                  placeholder="0"
                  data-testid="funding-form-cash-granted"
                  parseValue={parseWholeNumber}
                />
              </FormControl>
              <FieldErrorMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="requestedAmount"
          render={({ field }) => (
            <FormItem className={cn("relative h-fit w-full gap-1 mb-5.5")}>
              <CustomLabel
                htmlFor="requestedAmount"
                label={amountLabel("Food credit", currency)}
              />
              <FormControl>
                <FormattedInputField
                  nameField="requestedAmount"
                  field={field}
                  placeholder="0"
                  data-testid="funding-form-food-credit"
                  parseValue={parseWholeNumber}
                />
              </FormControl>
              <FieldErrorMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="cashToCreditConversionRate"
          render={({ field }) => (
            <FormItem className={cn("relative h-fit w-full gap-1 mb-5.5")}>
              <CustomLabel
                htmlFor="cashToCreditConversionRate"
                label={
                  <LabelWithOptional label="Cash to credit conversion rate" />
                }
              />
              <FormControl>
                <FormattedInputField
                  nameField="cashToCreditConversionRate"
                  field={field}
                  placeholder=""
                  allowDecimals
                  data-testid="funding-form-conversion-rate"
                  parseValue={parseDecimal}
                />
              </FormControl>
              <FieldErrorMessage />
            </FormItem>
          )}
        />

        <DatePickerControl
          control={control}
          name="contractStartDate"
          label={<LabelWithOptional label="Contract start date" />}
          placeholder="mm/dd/yyyy"
          classes="mb-5.5"
          clearable
        />

        <FormField
          control={control}
          name="contractDurationMonths"
          render={({ field }) => (
            <FormItem className={cn("relative h-fit w-full gap-1 mb-5.5")}>
              <CustomLabel
                htmlFor="contractDurationMonths"
                label={<LabelWithOptional label="Contract duration (months)" />}
              />
              <FormControl>
                <FormattedInputField
                  nameField="contractDurationMonths"
                  field={field}
                  placeholder="0"
                  data-testid="funding-form-contract-duration"
                  parseValue={parseWholeNumber}
                />
              </FormControl>
              <FieldErrorMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};

export default FundingCreditFields;
